import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal } from "../Modal";
import {
    AddRemoveLicenseKeyModalContainer,
    AddRemoveLicenseKeyModalContentContainer,
    AddRemoveLicenseKeyModalTitle,
    AddRemoveLicenseKeyModalContentWrapper,
    Form,
    Button,
    SubTitle,
    Select,
    ButtonsWrapper,
} from "./AddRemoveLicenseKey.styles";
import {
    GetLicensesType,
    License,
} from "../../../core/ServerApiLayer/VOne/institutions/get_available_licenses";
import {
    AssignLicenseType,
    UnassignLicenseType,
} from "../../../core/ServerApiLayer/VOne/licenses/assign";
import { getJwt } from "../../../core/LocalStorage/AuthSession/core";

// Language Context
import { useLanguage } from "../../../core/translations/LanguageContext";

// Translations
import { AddRemoveLicenseModalTranslations } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import { SupportedLanguage } from "../../../core/translations/supportedLanguages";

const TRANSLATIONS: Record<
    SupportedLanguage,
    AddRemoveLicenseModalTranslations
> = {
    en: enTranslations,
    de: deTranslations,
};

// What a license mutation reports back, so the modal knows whether to close or
// to stay open and show what went wrong.
export interface LicenseMutationResult {
    ok: boolean;
    error?: string;
}

interface AddRemoveLicenseKeyModalProps {
    onRequestClose: () => void;
    selectedLicenseKeyRow: any;
    getLicenses: GetLicensesType;
    handleAssignLicense: (
        userId: number,
        licenseKey: string,
    ) => Promise<LicenseMutationResult>;
    handleUnassignLicense: (
        userId: number,
        licenseKey: string,
    ) => Promise<LicenseMutationResult>;
}

const AddRemoveLicenseKeyModal = ({
    selectedLicenseKeyRow,
    onRequestClose,
    getLicenses,
    handleAssignLicense,
    handleUnassignLicense,
}: AddRemoveLicenseKeyModalProps) => {
    const { language } = useLanguage();
    const translations = TRANSLATIONS[language];
    const { institutionId, departmentId } = useParams<{
        institutionId: string;
        departmentId?: string;
    }>();

    const [licenses, setLicenses] = useState<License[]>([]);
    const [chosenLicense, setChosenLicense] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchLicenses = async () => {
            const jwt = getJwt();
            if (!jwt) return;

            const response = await getLicenses(
                jwt,
                Number(institutionId),
                departmentId ? Number(departmentId) : undefined,
            );
            if (response.status === 200 && response.licenses) {
                // Only offer licenses that are available AND not already
                // assigned to someone — otherwise you could hand out a licence
                // that is already in use.
                setLicenses(
                    response.licenses.filter(
                        (license) => license.available && !license.assigned_to,
                    ),
                );
            }
        };

        fetchLicenses();
    }, [institutionId, departmentId, getLicenses]);

    // Run a license change, then get out of the way. The handler only resolves
    // once the users table has been re-read from the server, so by the time the
    // modal closes the row behind it is already up to date — leaving the modal
    // open over a stale-looking table is what made this look like nothing had
    // happened at all.
    const submit = async (mutate: () => Promise<LicenseMutationResult>) => {
        setLoading(true);
        setError(null);

        const result = await mutate();

        setLoading(false);
        if (result.ok) {
            onRequestClose();
        } else {
            setError(result.error ?? "Something went wrong. Please try again.");
        }
    };

    return (
        <Modal onRequestClose={onRequestClose}>
            <AddRemoveLicenseKeyModalContainer>
                <AddRemoveLicenseKeyModalContentContainer>
                    <AddRemoveLicenseKeyModalContentWrapper>
                        {error && <p style={{ color: "red" }}>{error}</p>}

                        {!selectedLicenseKeyRow.license_key ? (
                            <Form>
                                <AddRemoveLicenseKeyModalTitle>
                                    {translations.addNewTitle}
                                </AddRemoveLicenseKeyModalTitle>
                                <Select
                                    id="selectedLicense"
                                    value={chosenLicense} // Ensure it's controlled
                                    onChange={(e) => {
                                        setChosenLicense(e.target.value);
                                    }}
                                    disabled={loading}
                                >
                                    <option value="">Select a License</option>{" "}
                                    {/* Default option */}
                                    {licenses.map((license) => (
                                        <option
                                            key={license.id}
                                            value={license.license_key}
                                        >
                                            {license.name} -{" "}
                                            {license.license_key}
                                        </option>
                                    ))}
                                </Select>
                                <Button
                                    type="button"
                                    backgroundColorHex="#102c46"
                                    onClick={() =>
                                        submit(() =>
                                            handleAssignLicense(
                                                selectedLicenseKeyRow.id,
                                                chosenLicense,
                                            ),
                                        )
                                    }
                                    disabled={loading || !chosenLicense}
                                >
                                    {loading
                                        ? translations.working
                                        : translations.add}
                                </Button>
                            </Form>
                        ) : (
                            <Form>
                                <AddRemoveLicenseKeyModalTitle>
                                    {translations.title}
                                </AddRemoveLicenseKeyModalTitle>
                                <SubTitle>
                                    {translations.removalTitle}{" "}
                                    {selectedLicenseKeyRow.email}
                                </SubTitle>
                                <ButtonsWrapper>
                                    <Button
                                        type="button"
                                        backgroundColorHex="#102c46"
                                        onClick={() =>
                                            submit(() =>
                                                handleUnassignLicense(
                                                    selectedLicenseKeyRow.id,
                                                    selectedLicenseKeyRow.license_key,
                                                ),
                                            )
                                        }
                                        disabled={loading}
                                    >
                                        {loading
                                            ? translations.working
                                            : translations.add}
                                    </Button>
                                    <Button
                                        type="button"
                                        backgroundColorHex="#000"
                                        onClick={onRequestClose}
                                        disabled={loading}
                                    >
                                        {translations.cancel}
                                    </Button>
                                </ButtonsWrapper>
                            </Form>
                        )}
                    </AddRemoveLicenseKeyModalContentWrapper>
                </AddRemoveLicenseKeyModalContentContainer>
            </AddRemoveLicenseKeyModalContainer>
        </Modal>
    );
};

export default AddRemoveLicenseKeyModal;
